import app from 'flarum/forum/app';
import { voiceApi as api } from './api';
import deviceAlert from './deviceAlert';

/**
 * The one voice chat this tab is in, if any. Reached as `app.parleyVoice`.
 */
export default class Voice {
  constructor() {
    this.session = null;
    this.joining = null;
    this.rosters = {}; // roomId → { at, participants } for rooms we are not in
  }
  boot() {
    window.addEventListener('pagehide', () => this.session && api.beaconLeave(this.session.roomId));
    // A reload cannot carry a voice chat across; leave cleanly.
    api.current().then(r => r && r.roomId && api.leave(r.roomId));
  }
  bind(channel) {
    const mine = d => this.session && this.session.roomId === d.roomId;
    channel.bind('parley.voice.joined', d => mine(d) && this.session.joined(d.participant));
    channel.bind('parley.voice.left', d => mine(d) && this.session.left(d.userId));
    channel.bind('parley.voice.muted', d => mine(d) && this.session.mutedChanged(d.userId, d.muted));
    channel.bind('parley.voice.signal', d => mine(d) && this.session.receive(d));
  }
  in(roomId) {
    return !!this.session && this.session.roomId === Number(roomId);
  }
  async join(room) {
    if (this.in(room.id) || this.joining) return;
    if (app.parleyCalls && app.parleyCalls.session) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('ernestdefoe-parley-calls.forum.voice.on_a_call'));
      return;
    }
    this.leave();
    this.joining = room.id;
    m.redraw();
    let s = null;
    try {
      // The voice engine is a chunk of its own, fetched the first time
      // someone joins a room's voice chat, not shipped on every page.
      const {
        default: VoiceSession
      } = await import(/* webpackChunkName: 'forum/VoiceSession', webpackMode: 'lazy-once' */ './VoiceSession');
      s = new VoiceSession(room);
      await s.start();
      this.session = s;
      // Talking in a room is being in it.
      if (app.parley.room(room.id) && !app.parley.room(room.id).joined) app.parley.openRoom(room.id);
    } catch (e) {
      s?.close(false);
      if (e && e.name && /NotAllowed|NotFound|NotReadable/.test(e.name)) {
        deviceAlert('no_microphone');
      }
      // Anything else (full, no permission) was explained by Flarum's alert.
    } finally {
      this.joining = null;
      app.parley.beat();
      m.redraw();
    }
  }
  leave() {
    if (!this.session) return;
    this.session.close();
    this.session = null;
    app.parley.beat();
    m.redraw();
  }

  /** The server ended this session without us asking: drop it, and say so. */
  ended(session) {
    if (this.session !== session) return;
    session.close(false);
    this.session = null;
    app.alerts.show({
      type: 'info'
    }, app.translator.trans('ernestdefoe-parley-calls.forum.voice.ended'));
    app.parley.beat();
    m.redraw();
  }

  /** Who is in a room's voice, for a room we are not in: fetched, cached briefly. */
  rosterOf(roomId) {
    if (this.in(roomId)) return [...this.session.roster.values()];
    const cached = this.rosters[roomId];
    if (!cached || Date.now() - cached.at > 10000) {
      this.rosters[roomId] = {
        at: Date.now(),
        participants: cached ? cached.participants : []
      };
      api.roster(roomId).then(r => {
        if (r) {
          this.rosters[roomId] = {
            at: Date.now(),
            participants: r.participants
          };
          m.redraw();
        }
      });
    }
    return this.rosters[roomId].participants;
  }
}flarum.reg.addChunkModule('ernestdefoe-parley-calls:forum/VoiceSession', '431', 'ernestdefoe-parley-calls', 'forum/VoiceSession');